// Loyalty Points Phase 1 (Sep 2026) — the customer's own points view.
//
// Balance, what is spendable right now, and a plain-language history of
// every movement. It is the customer-facing half of the approved "basic
// history" scope; the tenant-admin view (`CommerceSettings` member
// drawer) shows the same ledger with the operational detail this one
// deliberately omits.
//
// Access is the SAME OTP-verified proof `PastOrdersLookup` uses, and for
// the same reason: points are money. A typed phone number is not
// identity, so it can never reveal or spend a balance — the backend
// enforces that independently in `routes/retail_loyalty.py`, and nothing
// here is reachable by order code.
//
// Every figure shown comes from the server. Nothing is recomputed
// client-side, because a balance the customer disputes has to match the
// ledger exactly.
import React, { useCallback, useEffect, useState } from "react";
import { X, Sparkles, RefreshCw } from "lucide-react";
import axios from "axios";

// Sep 2026 (O-9) — bare `axios` with an explicit base, NOT `lib/api`.
//
// This is a PUBLIC storefront component. `lib/api` is the authenticated
// ADMIN client: it is created at module load with `axios.create()`, it
// attaches the dashboard bearer token from `localStorage`, and its 401
// handler clears that token and broadcasts `auth:session-expired`. None
// of that belongs on a customer's checkout, which authenticates with the
// `checkout_attempt_token` in the request body instead.
//
// It also broke sixteen pre-existing test suites. Every sibling in this
// folder mocks `axios` as `{ get, post }`, which is all they ever needed;
// pulling `lib/api` into this module's graph meant `axios.create` was
// suddenly called at import time and every CheckoutModal and WelcomeModal
// suite failed to load with `_axios.default.create is not a function` —
// 16 suites, 0 tests run, before a single assertion.
//
// The URL is unchanged: `lib/api` sets baseURL to `${BACKEND_URL}/api`,
// so `/public/retail/...` resolved to exactly the path below, which is
// what `routes/retail_loyalty.py` serves (`APIRouter(prefix="/api")` plus
// `/public/retail/{slug}/loyalty/...`). Note the segment order is
// `public/retail`, unlike the older `retail/public` endpoints — that is
// the backend's actual shape, not a typo.
const LOYALTY_PUBLIC_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/retail`;

const PAGE_SIZE = 25;

// Plain language for each ledger entry type. The customer sees what
// happened to their points, never the internal entry name.
const ENTRY_LABEL = {
  earn: "Points earned",
  redeem_finalized: "Points used",
  redeem_reserved: "Points held for an order",
  redeem_released: "Held points returned",
  earn_reversal: "Points reversed — refund",
  redeem_restored: "Points returned — refund",
  manual_credit: "Points added by the restaurant",
  manual_debit: "Points removed by the restaurant",
};

const describe = (item) =>
  ENTRY_LABEL[item.type] || "Points adjustment";

export default function LoyaltyPointsPanel({
  slug,
  catalogId,
  sessionId,
  verifiedProof,
  onClose,
}) {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Loyalty checkout visibility fix (Sep 2026) — EXACTLY the fields
  // `routes/retail_loyalty.py`'s `_MemberAuthIn` subclasses declare, and
  // no others. That model sets `model_config = {"extra": "forbid"}` as a
  // deliberate security control (no caller may smuggle a `company_id`),
  // and it has no `session_id` field. Sending one made FastAPI reject
  // every summary AND history call here with 422 before the handler ran,
  // so "My Points" could only ever report "unavailable". `sessionId`
  // remains a prop the caller passes; it is simply not part of this
  // endpoint's contract.
  const proofBody = useCallback(
    () => ({
      catalog_id: catalogId,
      checkout_attempt_token: verifiedProof?.token,
      destination_channel: verifiedProof?.channel,
      destination_value: verifiedProof?.destinationValue,
    }),
    [catalogId, verifiedProof],
  );

  const load = useCallback(
    async (nextOffset = 0) => {
      if (!verifiedProof?.token) {
        setError("verification_required");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const [summaryRes, historyRes] = await Promise.all([
          axios.post(`${LOYALTY_PUBLIC_BASE}/${slug}/loyalty/summary`, proofBody()),
          axios.post(`${LOYALTY_PUBLIC_BASE}/${slug}/loyalty/history`, {
            ...proofBody(),
            limit: PAGE_SIZE,
            offset: nextOffset,
          }),
        ]);
        setSummary(summaryRes.data);
        setItems(historyRes.data?.items || []);
        setTotal(Number(historyRes.data?.total || 0));
        setOffset(nextOffset);
      } catch (e) {
        setError(
          e?.response?.status === 401 || e?.response?.status === 403
            ? "verification_required"
            : "unavailable",
        );
      } finally {
        setLoading(false);
      }
    },
    [slug, proofBody, verifiedProof],
  );

  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, verifiedProof?.token]);

  const balance = Number(summary?.balance_points || 0);
  const available = Number(summary?.available_points || 0);
  const reserved = Number(summary?.reserved_points || 0);
  const isNegative = !!summary?.is_negative;
  const canPageBack = offset > 0;
  const canPageForward = offset + PAGE_SIZE < total;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="loyalty-points-title"
      data-testid="loyalty-points-panel"
    >
      <div className="bg-white w-full sm:max-w-lg sm:rounded-lg rounded-t-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2
            id="loyalty-points-title"
            className="font-semibold flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4 text-emerald-700" aria-hidden="true" />
            My points
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close points"
            className="p-1 rounded hover:bg-stone-100"
            data-testid="loyalty-points-close"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading && (
            <div
              className="py-10 text-center text-sm text-stone-500"
              data-testid="loyalty-points-loading"
            >
              Loading your points…
            </div>
          )}

          {!loading && error === "verification_required" && (
            <div
              className="py-10 text-center text-sm text-stone-600"
              data-testid="loyalty-points-verification-required"
            >
              Please verify your phone to see your points.
            </div>
          )}

          {!loading && error === "unavailable" && (
            <div className="py-10 text-center" data-testid="loyalty-points-error">
              <p className="text-sm text-stone-600">
                Your points aren’t available right now.
              </p>
              <button
                type="button"
                onClick={() => load(offset)}
                className="mt-3 inline-flex items-center gap-1.5 text-sm underline text-emerald-800"
                data-testid="loyalty-points-retry"
              >
                <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
                Try again
              </button>
            </div>
          )}

          {!loading && !error && summary && (
            <>
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4">
                <div className="text-3xl font-bold text-emerald-900" data-testid="loyalty-points-available">
                  {available}
                </div>
                <div className="text-xs text-emerald-800/80">
                  point{available === 1 ? "" : "s"} you can use now
                </div>
                {reserved > 0 && (
                  <div
                    className="text-xs text-emerald-800/80 mt-1.5"
                    data-testid="loyalty-points-reserved"
                  >
                    {reserved} more {reserved === 1 ? "is" : "are"} being held for an
                    order you haven’t finished paying for.
                  </div>
                )}
                {isNegative && (
                  <div
                    className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2"
                    data-testid="loyalty-points-negative"
                  >
                    Your balance is {balance}. A refunded order returned points that had
                    already been spent. You can earn back to zero — nothing else is owed.
                  </div>
                )}
              </div>

              <h3 className="text-sm font-medium mt-4 mb-1.5">History</h3>
              {items.length === 0 ? (
                <div
                  className="py-8 text-center text-sm text-stone-500"
                  data-testid="loyalty-points-history-empty"
                >
                  No points activity yet. Points are added after an order is paid for.
                </div>
              ) : (
                <ul className="divide-y" data-testid="loyalty-points-history">
                  {items.map((item, i) => {
                    const change = Number(item.points_change || 0);
                    const held = item.type === "redeem_reserved" || item.type === "redeem_released";
                    return (
                      <li
                        key={`${item.date}-${i}`}
                        className="py-2.5 flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="text-sm">{describe(item)}</div>
                          <div className="text-xs text-stone-500">
                            {item.date ? new Date(item.date).toLocaleDateString() : ""}
                            {item.order_code ? ` · Order ${item.order_code}` : ""}
                          </div>
                          {item.reason && (
                            <div className="text-xs text-stone-500 italic">{item.reason}</div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          {held ? (
                            <span className="text-xs text-stone-500">held</span>
                          ) : (
                            <span
                              className={
                                change > 0
                                  ? "text-sm font-semibold text-emerald-700"
                                  : "text-sm font-semibold text-stone-700"
                              }
                            >
                              {change > 0 ? `+${change}` : change}
                            </span>
                          )}
                          <div className="text-[11px] text-stone-400">
                            balance {item.balance_after}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {(canPageBack || canPageForward) && (
                <div className="flex items-center justify-between pt-3">
                  <button
                    type="button"
                    disabled={!canPageBack}
                    onClick={() => load(Math.max(0, offset - PAGE_SIZE))}
                    className="text-sm underline disabled:opacity-40 disabled:no-underline"
                    data-testid="loyalty-points-prev"
                  >
                    Newer
                  </button>
                  <span className="text-xs text-stone-500">
                    {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
                  </span>
                  <button
                    type="button"
                    disabled={!canPageForward}
                    onClick={() => load(offset + PAGE_SIZE)}
                    className="text-sm underline disabled:opacity-40 disabled:no-underline"
                    data-testid="loyalty-points-next"
                  >
                    Older
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
